import React, {SyntheticEvent, useEffect, useState} from 'react';
import {LoyaltyNameDisplay, LoyaltySummaryBalance, WebSnippetProfile} from "../Helpers/Types";
import TextInput from "../TextInput";
import useGetLoyaltySummary from "../Utils/UseGetLoyaltySummary";
import {useSnippetContext} from "../Utils/UseSnippetContext";
import useServiceUrls from "../Utils/UseServiceUrls";
import LoadingSpinner from "../LoadingSpinner";
import SnippetHeader from "../Helpers/SnippetHeader";
import SnippetFooter from "../Helpers/SnippetFooter";
import SignupWrapper from "../SignupWrapper";

type LoyaltySummarySnippetProps = {
    snippetProfile: WebSnippetProfile
}

export default function LoyaltySummarySnippet(props: LoyaltySummarySnippetProps) {
    const config = props.snippetProfile.LoyaltySummary
    const [value, setValue] = useState('')
    const [changed, setChanged] = useState(false)
    const [showSignup, setShowSignup] = useState(false)
    // @ts-ignore
    const {domain, deviceCode} = useSnippetContext();
    const {serviceUrls} = useServiceUrls(domain);
    const field = config?.ResolutionField ?? 'Email'
    const {status, summary, error, refetch, isFetching} = useGetLoyaltySummary(serviceUrls, field, value, domain, deviceCode)

    const showResult = summary && summary.Found && !changed

    useEffect(() => {
        if (!showResult)
            return;

        const seconds = config?.AutoLogoutSeconds ?? 0
        if (!seconds || seconds <= 0)
            return;

        const timer = setTimeout(() => {
            setValue('')
            setChanged(true)
        }, seconds * 1000)

        return () => clearTimeout(timer)
    }, [showResult, config])

    const handleChange = (fieldName: string, newValue: string) => {
        setValue(newValue)
        setChanged(true)
    }

    const search = (e: SyntheticEvent) => {
        e.preventDefault()
        setChanged(false)
        refetch()
    }

    if (showSignup && config?.SignupSnippetCode)
        return <SignupWrapper snippetCode={config.SignupSnippetCode} setShowSignup={setShowSignup} />

    const selectedClasses = config?.LoyaltyAccountClassUids ?? []
    const balances: Array<LoyaltySummaryBalance> = (summary?.Balances ?? [])
        .filter((b: LoyaltySummaryBalance) => selectedClasses.length === 0 || selectedClasses.includes(b.ClassUid))

    const showName = () => {
        if (!summary) return null
        if (config?.NameDisplay === LoyaltyNameDisplay.FirstAndLast)
            return `${summary.FirstName ?? ''} ${summary.LastName ?? ''}`.trim()
        if (config?.NameDisplay === LoyaltyNameDisplay.FirstOnly)
            return summary.FirstName ?? ''
        return null
    }

    return (
        <>
            <SnippetHeader logoUrl={props.snippetProfile?.LogoUrl ?? ''} title={props.snippetProfile?.Title ?? ''} message={props.snippetProfile?.Message ?? ''} snippetCode={props.snippetProfile?.Code ?? ''}/>

            {!showResult &&
                <>
                    <TextInput id={'LoyaltySummaryInput'} value={value} label={config?.InputLabel ? config.InputLabel : 'Enter your information'} handleChange={handleChange} />
                    <button className='bl-snippet-button' onClick={search}>{config?.SearchButtonLabel ? config.SearchButtonLabel : 'Search'}</button>
                    {config?.AllowSignup && config?.SignupSnippetCode &&
                        <button className='bl-snippet-button' onClick={() => setShowSignup(true)}>{config?.SignupButtonLabel ? config.SignupButtonLabel : 'Sign Up'}</button>
                    }
                    {summary && !summary.Found && !changed &&
                        <p className='bLoyal-error-text'>{config?.CustomerNotFoundMessage ? config.CustomerNotFoundMessage : 'We could not find your account.'}</p>
                    }
                </>
            }

            {showResult &&
                <div className='bLoyal-center'>
                    {showName() && <h3>{showName()}</h3>}
                    {config?.ShowMobilePhone && summary.MobilePhone && <p>{summary.MobilePhone}</p>}
                    {config?.ShowEmail && summary.EmailAddress && <p>{summary.EmailAddress}</p>}
                    {config?.ShowCode && summary.Code && <p>{summary.Code}</p>}

                    {config?.ShowPointsBalances &&
                        <div>
                            {balances.map((b: LoyaltySummaryBalance) => (
                                <p key={b.ClassUid}>{b.ClassName}: {b.Balance}</p>
                            ))}
                        </div>
                    }

                    {config?.ShowCoupons && summary.Coupons && summary.Coupons.length > 0 &&
                        <div>
                            {summary.Coupons.map((c: any, index: number) => (
                                <div key={c.Uid ?? index} className='bLoyal-loyalty-summary-coupon'>
                                    <strong>{c.Name}</strong>
                                    {(c.WebSummary || c.Description) && <p>{c.WebSummary || c.Description}</p>}
                                </div>
                            ))}
                        </div>
                    }
                </div>
            }

            {(status === 'loading' || isFetching) && <LoadingSpinner />}
            {status === 'error' && <p className='bLoyal-error-text'>Unable to load loyalty summary.</p>}

            <SnippetFooter footer={props.snippetProfile?.Footer ?? ''} snippetCode={props.snippetProfile?.Code ?? ''}/>
        </>
    )
}
